/**
 * ユーザー詳細 `/idp/users/:userId`(固有 URL の全画面)。
 *
 * **正は Keycloak**。この画面は読んで見せ、編集は Drawer(`UserEditDrawer`)で送るだけ。
 * ヘッダー: 状態 / MFA のタグ、パスワード再設定 / 編集 / 無効化、「その他」に削除。
 * サマリー帯: 最新ログイン / 適用ポリシー / 権限 / 端末証明書 / セッション。
 * タブ: 概要 / 属性 / グループとポリシー / 端末証明書(operator 以上 + `idp.device-cert`)
 * / ログ(`idp.logs`)。機能が契約に無ければタブも問い合わせも出さない(関門は API)。
 *
 * 出していないもの: 非常用トークン、連携先アカウント(API が無い)、従業員への
 * リンク(ユーザーと従業員を結ぶ API が無い)。
 */
import { useMutation } from "@tanstack/react-query";
import { Alert, Button, Dropdown, Modal, Spin, message } from "antd";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { hasFeature } from "../../lib/features";
import { useApi, useMe } from "../../lib/useApi";
import { DetailPage, Initials, StatusTag, relativeTime, type SummaryItem } from "../../ui";
import { DeviceCertTab } from "./DeviceCertTab";
import { useUserDeviceCerts } from "./deviceCertHooks";
import { errorText, useIdpPerms, useIdpUser, useInvalidateIdp } from "./hooks";
import { UserEditDrawer } from "./UserEditDrawer";
import { DeleteUserModal, ResetPasswordModal } from "./userDialogs";
import { userStatusTone } from "./userOptions";
import { AttributesTab } from "./userTabs/AttributesTab";
import { LogsTab } from "./userTabs/LogsTab";
import { OverviewTab } from "./userTabs/OverviewTab";
import { PolicyTab } from "./userTabs/PolicyTab";

const BACK = { to: "/idp/users", label: "ユーザー一覧へ戻る" };

export function UserDetailPage() {
  const { userId = "" } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const api = useApi();
  const invalidate = useInvalidateIdp();
  const { canAdmin, canRun } = useIdpPerms();
  const me = useMe();
  const certsOk = canRun && hasFeature(me.data, "idp.device-cert");
  const logsOk = hasFeature(me.data, "idp.logs");
  const query = useIdpUser(userId || null);
  const certs = useUserDeviceCerts(userId || null, certsOk);
  const [editing, setEditing] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const u = query.data;

  const toggle = useMutation({
    mutationFn: (enabled: boolean) => api.updateIdpUser(userId, { enabled }),
    onSuccess: (_, enabled) => {
      invalidate();
      void message.success(enabled ? "有効にした" : "無効にした");
    },
    onError: (e) => void message.error(errorText(e)),
  });

  if (query.isError) {
    return (
      <Alert
        type="error"
        showIcon
        message="ユーザーを取得できなかった"
        description={
          <>
            {errorText(query.error)} <Link to={BACK.to}>{BACK.label}</Link>
          </>
        }
      />
    );
  }
  if (!u) {
    return (
      <div style={{ padding: 48, textAlign: "center" }}>
        <Spin />
      </div>
    );
  }

  const certCount = certs.data?.items.filter((c) => c.status !== "revoked").length;
  const summary: SummaryItem[] = [
    {
      label: "最新ログイン",
      value: u.accessInfo.lastLogin ? relativeTime(u.accessInfo.lastLogin) : "",
      empty: "なし",
    },
    {
      label: "適用ポリシー",
      value: u.policy?.effectiveGroupName
        ? `${u.policy.effectiveGroupName}${u.policy.effectiveIsDefault ? "(既定)" : ""}`
        : "",
      empty: "なし(素通り)",
    },
    { label: "権限", value: u.access.effectiveRoles.length ? `${u.access.effectiveRoles.length} 件` : "", empty: "なし" },
    // 取得失敗と 0 枚は別物(「なし」と出すと失効漏れに見えない)
    ...(certsOk
      ? [
          {
            label: "端末証明書",
            value: certs.isError ? "取得できない" : certCount ? `${certCount} 枚` : "",
            empty: "なし",
          },
        ]
      : []),
    { label: "セッション", value: `${u.accessInfo.sessions} 件` },
  ];

  const confirmToggle = () =>
    Modal.confirm({
      title: u.enabled ? `${u.username} を無効にする` : `${u.username} を有効にする`,
      content: u.enabled
        ? "ログインできなくなる(セッションは残る。切るなら「強制ログアウト」)。"
        : "ログインできるようになる。",
      okText: u.enabled ? "無効にする" : "有効にする",
      okButtonProps: { danger: u.enabled },
      cancelText: "やめる",
      onOk: () => toggle.mutateAsync(!u.enabled),
    });

  return (
    <>
      <DetailPage
        back={BACK}
        avatar={<Initials name={u.displayName || u.username} size="lg" />}
        title={u.displayName || u.username}
        tags={
          <>
            <StatusTag tone={userStatusTone(u.status)} dot>
              {u.status}
            </StatusTag>
            <StatusTag tone={u.otp.registered ? "success" : "neutral"}>
              {u.otp.registered ? "MFA あり" : "MFA なし"}
            </StatusTag>
          </>
        }
        sub={
          <>
            <span className="ui-num">{u.username}</span>
            {u.email && <span>{u.email}</span>}
          </>
        }
        actions={
          canAdmin && (
            <>
              <Button onClick={() => setResetting(true)}>パスワードを再設定</Button>
              <Button onClick={confirmToggle} loading={toggle.isPending}>
                {u.enabled ? "無効化" : "有効化"}
              </Button>
              <Button type="primary" onClick={() => setEditing(true)}>
                編集
              </Button>
              <Dropdown
                trigger={["click"]}
                menu={{
                  items: [{ key: "delete", label: "このユーザーを削除", danger: true }],
                  onClick: ({ key }) => key === "delete" && setDeleting(true),
                }}
              >
                <Button aria-label="その他">…</Button>
              </Dropdown>
            </>
          )
        }
        summary={summary}
        tabs={[
          { key: "overview", label: "概要", children: <OverviewTab user={u} canAdmin={canAdmin} /> },
          { key: "attrs", label: "属性", children: <AttributesTab user={u} /> },
          { key: "policy", label: "グループとポリシー", children: <PolicyTab user={u} canAdmin={canAdmin} /> },
          // 端末証明書の一覧は operator 以上 + 機能 `idp.device-cert`。**viewer にはタブごと出さない**
          {
            key: "certs",
            label: "端末証明書",
            badge: certCount,
            hidden: !certsOk,
            children: <DeviceCertTab userId={u.id} />,
          },
          { key: "logs", label: "ログ", hidden: !logsOk, children: <LogsTab user={u} /> },
        ]}
      />
      {canAdmin && (
        <>
          <UserEditDrawer user={u} open={editing} onClose={() => setEditing(false)} />
          <ResetPasswordModal id={u.id} username={u.username} open={resetting} onClose={() => setResetting(false)} />
          <DeleteUserModal
            id={u.id}
            username={u.username}
            open={deleting}
            onClose={() => setDeleting(false)}
            onDone={() => navigate(BACK.to)}
          />
        </>
      )}
    </>
  );
}
